import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { api, apiErrorFromResponse, authFetch } from '@/api/client'
import { isMissingRoute as isMissingOAuthRoute } from '@/api/endpoints/oauth'
import { queryKeys } from '@/api/queryKeys'
import { isApiError } from '@/lib/errors'
import type { components } from '@/../types/api'

/**
 * Destinos por canal (PR #309): as plataformas que espelham o que o YouTube
 * publica. A conta é conectada na tela do canal; a config e a fila ficam na
 * aba "Destinos" do modal do canal no Scheduler. Ver
 * apps/backend/docs/plan-platform-destinations.md.
 *
 * O front sai no merge (Cloudflare Pages) e o backend só no pull do skab: por
 * um tempo, estas rotas respondem 404, ou com campos faltando. Nos dois casos
 * as leituras devolvem ``null`` e a tela esconde a parte de destinos, sem erro.
 */
export type ChannelDestinations = components['schemas']['ChannelDestinationsOut']
export type InstagramDestination = components['schemas']['InstagramDestinationOut']
export type InstagramDestinationIn = components['schemas']['InstagramDestinationIn']
export type InstagramOptions = components['schemas']['InstagramOptions']
export type DestinationAccount = components['schemas']['DestinationAccountOut']
export type DestinationPublisher = components['schemas']['DestinationPublisherOut']
export type DestinationPost = components['schemas']['DestinationPostOut']
export type DestinationPostStatus = DestinationPost['status']
export type DestinationPlatform = DestinationPost['platform']
export type PauseWindow = components['schemas']['PauseWindow']
export type PageMeta = components['schemas']['Meta']

export interface DestinationPostsPage {
  data: DestinationPost[]
  meta: PageMeta
}

// ---------------------------------------------------------------------------
// Tolerância ao backend antigo
// ---------------------------------------------------------------------------

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isOptionalString(value: unknown): boolean {
  return value === undefined || value === null || typeof value === 'string'
}

function isAccount(value: unknown): boolean {
  if (value === undefined || value === null) return true
  if (!isRecord(value)) return false
  return (
    typeof value['username'] === 'string' &&
    typeof value['account_type'] === 'string' &&
    (value['status'] === 'active' || value['status'] === 'expired' || value['status'] === 'revoked') &&
    isOptionalString(value['expires_at'])
  )
}

function isInstagramOptions(value: unknown): boolean {
  if (!isRecord(value)) return false
  return (
    typeof value['caption_cta'] === 'string' &&
    typeof value['include_source_credit'] === 'boolean' &&
    typeof value['hashtags_max'] === 'number' &&
    typeof value['share_to_feed'] === 'boolean' &&
    typeof value['trial'] === 'boolean'
  )
}

function isDestination(value: unknown): boolean {
  if (!isRecord(value)) return false
  const publisher = value['publisher']
  return (
    typeof value['platform'] === 'string' &&
    typeof value['configured'] === 'boolean' &&
    typeof value['enabled'] === 'boolean' &&
    (value['timing_mode'] === 'with_primary' || value['timing_mode'] === 'offset') &&
    typeof value['offset_minutes'] === 'number' &&
    (value['max_per_day'] === null || typeof value['max_per_day'] === 'number') &&
    typeof value['require_safety_approved'] === 'boolean' &&
    Array.isArray(value['pause_windows']) &&
    isRecord(value['options']) &&
    isRecord(publisher) &&
    typeof publisher['registered'] === 'boolean' &&
    typeof publisher['enabled'] === 'boolean' &&
    isAccount(value['account'])
  )
}

/**
 * A resposta do ``GET``/``PUT .../destinations`` quando ela tem o formato que
 * esta tela conhece, ou ``null``. Sem o destino do Instagram completo, não há
 * o que mostrar: um backend anterior (ou posterior e incompatível) esconde a
 * seção em vez de exibir um formulário que gravaria pela metade.
 */
export function parseChannelDestinations(raw: unknown): ChannelDestinations | null {
  if (!isRecord(raw) || !Array.isArray(raw['destinations'])) return null
  const destinations = (raw['destinations'] as unknown[]).filter(isDestination)
  const instagram = destinations.find((d) => isRecord(d) && d['platform'] === 'instagram')
  if (!instagram || !isInstagramOptions((instagram as Record<string, unknown>)['options'])) return null
  return {
    channel_slug: typeof raw['channel_slug'] === 'string' ? raw['channel_slug'] : '',
    primary_platform: typeof raw['primary_platform'] === 'string' ? raw['primary_platform'] : 'youtube',
    destinations: destinations as ChannelDestinations['destinations'],
  }
}

/** O destino do Instagram de uma resposta já validada. */
export function instagramDestination(data: ChannelDestinations | null | undefined): InstagramDestination | null {
  const found = data?.destinations.find((d): d is InstagramDestination => d.platform === 'instagram')
  return found ?? null
}

/** A página da fila quando ela tem o formato esperado, ou ``null``. */
export function parseDestinationPosts(raw: unknown): DestinationPostsPage | null {
  if (!isRecord(raw) || !Array.isArray(raw['data']) || !isRecord(raw['meta'])) return null
  const meta = raw['meta']
  if (typeof meta['total'] !== 'number') return null
  const rows = (raw['data'] as unknown[]).filter(
    (r) =>
      isRecord(r) &&
      typeof r['id'] === 'number' &&
      typeof r['status'] === 'string' &&
      typeof r['publish_at'] === 'string',
  )
  return { data: rows as DestinationPost[], meta: meta as unknown as PageMeta }
}

/** Rota que ainda não existe neste backend: 404 (ou 405, se outra rota casar o caminho). */
function isMissingRoute(err: unknown): boolean {
  return isApiError(err) && (err.status === 404 || err.status === 405)
}

// ---------------------------------------------------------------------------
// Leituras
// ---------------------------------------------------------------------------

/** ``null`` quando o backend não conhece os destinos (ver o topo do arquivo). */
export async function fetchChannelDestinations(slug: string): Promise<ChannelDestinations | null> {
  try {
    const { data } = await api.GET('/api/v1/scheduler/channels/{slug}/destinations', {
      params: { path: { slug } },
    })
    return parseChannelDestinations(data?.data)
  } catch (err) {
    if (isMissingRoute(err)) return null
    throw err
  }
}

export function useChannelDestinations(slug: string | null | undefined, enabled = true) {
  const safeSlug = slug ?? ''
  return useQuery({
    queryKey: queryKeys.scheduler.destinations(safeSlug),
    queryFn: () => fetchChannelDestinations(safeSlug),
    enabled: enabled && Boolean(slug),
    staleTime: 15_000,
  })
}

export interface DestinationPostsParams {
  limit: number
  offset: number
  statuses?: readonly DestinationPostStatus[] | undefined
}

export async function fetchDestinationPosts(
  slug: string,
  params: DestinationPostsParams,
): Promise<DestinationPostsPage | null> {
  try {
    const { data } = await api.GET('/api/v1/scheduler/channels/{slug}/destination-posts', {
      params: {
        path: { slug },
        query: {
          limit: params.limit,
          offset: params.offset,
          ...(params.statuses?.length ? { status: [...params.statuses] } : {}),
        },
      },
    })
    return parseDestinationPosts(data)
  } catch (err) {
    if (isMissingRoute(err)) return null
    throw err
  }
}

/** A chave de uma página da fila (para ler o cache depois de uma ação). */
export function destinationPostsKey(slug: string, params: DestinationPostsParams) {
  return queryKeys.scheduler.destinationPosts(slug, {
    limit: params.limit,
    offset: params.offset,
    statuses: params.statuses ? [...params.statuses] : [],
  })
}

/** A fila e o histórico do canal, do horário mais novo para o mais velho. */
export function useDestinationPosts(slug: string, params: DestinationPostsParams, enabled = true) {
  return useQuery({
    queryKey: destinationPostsKey(slug, params),
    queryFn: () => fetchDestinationPosts(slug, params),
    enabled: enabled && Boolean(slug),
    staleTime: 10_000,
    // A fila anda sozinha (tick de 60 s): com a aba aberta, acompanha.
    refetchInterval: 30_000,
  })
}

// ---------------------------------------------------------------------------
// Escritas
// ---------------------------------------------------------------------------

/**
 * O que um PUT do Instagram leva: **só o que o dono mudou**. Desde o #309, um
 * campo ausente (ou ``null``) mantém o gravado, menos ``max_per_day``, em que
 * ``null`` quer dizer "todos". As opções são mescladas sobre as gravadas no
 * backend, então vão só as chaves mudadas — o tipo gerado as pede todas.
 */
export type InstagramDestinationPatch = Omit<InstagramDestinationIn, 'options'> & {
  options?: Partial<InstagramOptions>
}

/**
 * Grava o destino do Instagram com um PUT parcial. Mandar o formulário inteiro
 * gravaria um retrato velho por cima do servidor: um ``enabled: true`` do
 * formulário aberto antes do disjuntor religaria o destino e apagaria o motivo.
 *
 * Só resolve quando a resposta traz o que foi enviado — um backend que
 * respondesse 200 sem gravar viraria um "salvo" falso.
 */
export function useSaveInstagramDestination(slug: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: InstagramDestinationPatch): Promise<ChannelDestinations> => {
      const { data } = await api.PUT('/api/v1/scheduler/channels/{slug}/destinations', {
        params: { path: { slug } },
        // Opções parciais: o backend valida com os padrões e grava só as chaves enviadas.
        body: { destinations: [input as InstagramDestinationIn] },
      })
      const parsed = parseChannelDestinations(data?.data)
      if (!parsed || !savedAsSent(instagramDestination(parsed), input)) {
        throw new Error('O servidor respondeu sem gravar o destino. Tente de novo depois do deploy.')
      }
      return parsed
    },
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.scheduler.destinations(slug), data)
      // Desligar cancela a fila na hora; ligar pode reenfileirar pela varredura.
      void qc.invalidateQueries({ queryKey: queryKeys.scheduler.destinationPostsAll(slug) })
    },
  })
}

/** O valor enviado bate com o gravado — ou não foi enviado (ausente ou ``null`` = "não mexe"). */
function matches<T>(sent: T | null | undefined, saved: T): boolean {
  return sent === undefined || sent === null || sent === saved
}

/**
 * Se a resposta do PUT traz o destino como ele foi enviado, pela regra do PUT
 * parcial: campo ausente ou ``null`` não foi mudado, menos ``max_per_day``,
 * em que ``null`` é "todos". As pausas são comparadas pela quantidade: o
 * backend as devolve em UTC, e a tela as manda no fuso do upload.
 */
export function savedAsSent(saved: InstagramDestination | null, sent: InstagramDestinationPatch): boolean {
  if (!saved) return false
  const options = sent.options ?? {}
  return (
    matches(sent.enabled, saved.enabled) &&
    matches(sent.timing_mode, saved.timing_mode) &&
    matches(sent.offset_minutes, saved.offset_minutes) &&
    (!('max_per_day' in sent) || (saved.max_per_day ?? null) === (sent.max_per_day ?? null)) &&
    matches(sent.require_safety_approved, saved.require_safety_approved) &&
    (sent.pause_windows === undefined ||
      sent.pause_windows === null ||
      saved.pause_windows.length === sent.pause_windows.length) &&
    (Object.keys(options) as Array<keyof InstagramOptions>).every((key) => matches(options[key], saved.options[key]))
  )
}

export type DestinationPostAction = 'cancel' | 'retry'

/**
 * As opções da mutação de Cancelar/Tentar de novo, à parte para o teste. O
 * ``onSettled`` devolve a invalidação, e o React Query a espera: a mutação só
 * termina depois do refetch da fila, então a linha não volta a oferecer o
 * botão com o estado velho (um segundo clique ali só daria 409).
 */
export function destinationPostActionOptions(qc: QueryClient, slug: string) {
  return {
    mutationFn: async (input: { action: DestinationPostAction; postId: number }): Promise<DestinationPost> => {
      const path = { slug, post_id: input.postId }
      const { data } =
        input.action === 'cancel'
          ? await api.POST('/api/v1/scheduler/channels/{slug}/destination-posts/{post_id}/cancel', {
              params: { path },
            })
          : await api.POST('/api/v1/scheduler/channels/{slug}/destination-posts/{post_id}/retry', {
              params: { path },
            })
      if (!data) throw new Error('destination post action empty')
      return data.data
    },
    onSettled: () => qc.invalidateQueries({ queryKey: queryKeys.scheduler.destinationPostsAll(slug) }),
  }
}

/** Cancelar e Tentar de novo valem na hora — não dependem do Salvar do modal. */
export function useDestinationPostAction(slug: string) {
  const qc = useQueryClient()
  return useMutation(destinationPostActionOptions(qc, slug))
}

/**
 * Desconecta o Instagram do canal: apaga o token daqui, desliga o destino e
 * cancela os posts pendentes. Fica fora do ``/api/v1``, como o do YouTube,
 * porque mora com o callback do OAuth.
 */
export function useDisconnectInstagram() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (channel: string) => {
      const res = await authFetch(`/auth/instagram/disconnect?channel=${encodeURIComponent(channel)}`, {
        method: 'POST',
        credentials: 'same-origin',
      })
      if (!res.ok) {
        const body = (await res.clone().json().catch(() => ({}))) as { detail?: unknown }
        if (isMissingOAuthRoute(res.status, body.detail)) {
          throw new Error('Este servidor ainda não sabe desconectar o Instagram: chega com o próximo deploy do backend.')
        }
        throw await apiErrorFromResponse(res, 'Falha ao desconectar')
      }
    },
    onSuccess: (_data, channel) => {
      void qc.invalidateQueries({ queryKey: queryKeys.scheduler.destinations(channel) })
      void qc.invalidateQueries({ queryKey: queryKeys.scheduler.destinationPostsAll(channel) })
    },
  })
}
